import { useEffect, useRef, type PropsWithChildren } from 'react';
import { Menu } from 'lucide-react';
import { ActionIcon, Affix, Anchor, AppShell, em, Flex, Transition } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import ChangePassword from '@/modules/change-password/ChangePassword';
import UsersAgreements from '@/modules/users-agreements/UsersAgreements';
import { useGetUsersCurrent } from '@/services/users';
import { useAppStore } from '@/stores/app-store';
import Navbar from './navbar/Navbar';

const OnBoard = ({ children }: PropsWithChildren) => {
  const { data: dataUser } = useGetUsersCurrent();

  if (dataUser?.data?.is_first_login) {
    return <ChangePassword opened={dataUser?.data?.is_first_login} />;
  }
  if (!!dataUser?.data?.id && !dataUser?.data?.is_accept_agreement) {
    return <UsersAgreements opened={!dataUser?.data?.is_accept_agreement} />;
  }
  return children;
};

const DashboardLayout = ({ children }: PropsWithChildren) => {
  const { sideBarVisible } = useAppStore();
  const toggle = useAppStore((s) => s.sideBarToggle);
  const isMobile = useMediaQuery(`(max-width: ${em(750)})`, false, {
    getInitialValueInEffect: false,
  });

  const hasMounted = useRef(false);
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      if (isMobile) {
        if (sideBarVisible) {
          toggle();
        }
      } else {
        if (!sideBarVisible) {
          toggle();
        }
      }
    }
  }, [isMobile, sideBarVisible]);

  return (
    <AppShell
      header={{
        height: 0,
        collapsed: true,
      }}
      navbar={{
        width: isMobile ? '100vw' : 300,
        breakpoint: 'sm',
        collapsed: {
          mobile: isMobile && !sideBarVisible,
          desktop: !sideBarVisible,
        },
      }}
    >
      <Navbar />

      <AppShell.Main pos="relative">
        <OnBoard>{children}</OnBoard>
        <Affix position={{ top: `calc(96px / 2 - 14px)`, left: 0 }} zIndex={150}>
          <Transition transition="slide-left" mounted={!sideBarVisible}>
            {(transitionStyles) => (
              <ActionIcon style={transitionStyles} onClick={toggle} variant="default" color="dark">
                <Menu />
              </ActionIcon>
            )}
          </Transition>
        </Affix>

        <Flex
          justify="end"
          pr={16}
          pb={16}
          h={40}
          pos="fixed"
          bottom={0}
          right={0}
          bg="white"
          w="100%"
        >
          Developed with ❤️ by&nbsp;
          <Anchor href="https://www.batsolution.id/" target="_blank">
            BAT Solution
          </Anchor>
        </Flex>
      </AppShell.Main>
    </AppShell>
  );
};

export default DashboardLayout;
